import { useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { useAccessControl } from 'hooks/access-control';
import { useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';

import { CreditsBalanceDetails } from 'components/CreditsBalanceDetails/CreditsBalanceDetails';
import { UserAvatar } from 'components/UserAvatar/UserAvatar';

import { selectPermissions } from 'api/user-permissions-slice';

import { ProfileCard } from '../ProfileCard/ProfileCard';
import { SupportAccessMenu } from '../SupportAccessMenu/SupportAccessMenu';
import styles from './AppNavbar.module.scss';

export const AppNavbar = ({
  user,
  company,
  companyChecks,
  authentication,
  fillOnScroll,
  hideNavigation = false,
  onSignOut = () => {},
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const permissions = useSelector(selectPermissions);
  const hasAccess = useAccessControl(permissions);

  useEffect(() => {
    const handleScroll = e => {
      let target = e.target || e.srcElement;
      let document = target.scrollingElement || target.documentElement;
      var scrollTop = document.scrollTop;
      setScrolled(!!scrollTop);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleProfileCard = e => {
    if (e) e.preventDefault();
    setShowProfile(!showProfile);
  };

  const hasLegacyReferenceChecks = useMemo(() => {
    return (companyChecks || []).some(check => check.type === 'reference');
  }, [companyChecks]);

  const hasQuestionnaires = useMemo(() => {
    return (companyChecks || []).some(
      check => check.type === 'candidate_questionnaire' || check.type === 'custom_reference',
    );
  }, [companyChecks]);

  return (
    <div
      className={cn(
        'bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60',
        styles.root,
        { [styles.rootNotScrolled]: !scrolled && fillOnScroll },
      )}>
      <ProfileCard
        user={user}
        visible={showProfile}
        onClose={() => setShowProfile(false)}
        onSignOut={onSignOut}
      />
      <div className={styles.left}>
        <Link to="/dashboard" className={styles.logo}>
          <img
            className={cn(styles.logo, 'u-margin-bottom--0')}
            src="./assets/images/checkmate/icon-label.svg"
            alt="checkmate"
          />
        </Link>
        {!hideNavigation && (
          <>
            <div className={cn(styles.hideXS, styles.links)}>
              <NavLink to="/dashboard" className="u-link dark" data-testid="nav-dashboard-link">
                Dashboard
              </NavLink>
              {hasAccess(Permissions.FormsWrite) && hasLegacyReferenceChecks && (
                <NavLink to="/questionnaires" className="u-link dark">
                  Legacy Questionnaires
                </NavLink>
              )}
              {hasAccess(Permissions.FormsWrite) && hasQuestionnaires && (
                <NavLink
                  to="/questionnaires-v2"
                  className="u-link dark"
                  data-testid="nav-questionnaires-link">
                  Questionnaires
                </NavLink>
              )}
              <NavLink to="/reporting" className="u-link dark">
                Reporting
              </NavLink>
              {company.renewal_dashboard_enabled && hasAccess(Permissions.RenewalsWrite) && (
                <NavLink to="/renewals" className="u-link dark">
                  Renewals
                </NavLink>
              )}
              {authentication.admin && (
                <Link to="/admin/companies" data-testid="nav-admin-link">
                  Admin
                </Link>
              )}
            </div>
          </>
        )}
      </div>
      <div className={styles.right}>
        <SupportAccessMenu />
        <CreditsBalanceDetails companyChecks={companyChecks} />
        <UserAvatar onClick={toggleProfileCard}>{user ? user.first_name[0] : ''}</UserAvatar>
      </div>
    </div>
  );
};
